import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Separator } from '../../components/ui/separator';
import { Input } from '../../components/ui/input';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../components/ui/tooltip';
import {
  Users,
  UserPlus,
  Building2,
  CalendarDays,
  Loader2,
  UserCheck,
  Activity,
  KeyRound,
  Edit,
  Trash2,
  Upload,
  Download,
  FolderPlus,
  ArrowRight,
  Ban,
  GitBranch,
  Cpu,
  Bot,
  FileUp,
  Network,
  BellRing,
  History,
  AlertTriangle,
  TrendingUp,
  TrendingDown,
  BarChart3,
  FolderOpen,
  ChevronRight,
  ChevronDown,
  Shield,
  Zap,
  Target,
  Clock,
  CheckCircle2,
  XCircle,
  ArrowUpRight,
  Search,
  MoreHorizontal,
  Layers,
  PieChart,
  Gauge,
  Sparkles,
  Eye,
  UserX,
  Mail
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../../lib/api';

// Action icon/label maps for activity log
const ACTION_ICONS = {
  user_created: { icon: UserPlus, color: 'text-green-500' },
  user_deleted: { icon: Trash2, color: 'text-red-500' },
  role_changed: { icon: KeyRound, color: 'text-blue-500' },
  status_changed: { icon: Ban, color: 'text-amber-500' },
  password_reset: { icon: KeyRound, color: 'text-purple-500' },
  profile_updated: { icon: Edit, color: 'text-slate-500' },
  project_created: { icon: FolderPlus, color: 'text-green-500' },
  project_access_granted: { icon: UserPlus, color: 'text-blue-500' },
  project_access_revoked: { icon: Trash2, color: 'text-red-500' },
  bulk_import: { icon: Upload, color: 'text-indigo-500' },
  users_exported: { icon: Download, color: 'text-cyan-500' },
  org_settings_updated: { icon: Building2, color: 'text-amber-500' },
};

const ACTION_LABELS = {
  user_created: 'created account for',
  user_deleted: 'deleted user',
  role_changed: 'changed role of',
  status_changed: 'changed status of',
  password_reset: 'reset password for',
  profile_updated: 'updated profile of',
  project_created: 'created project',
  project_access_granted: 'granted project access to',
  project_access_revoked: 'revoked project access from',
  bulk_import: 'bulk imported users',
  users_exported: 'exported users',
  org_settings_updated: 'updated organization settings',
};

// Reusable Sub-components

const StatCard = ({ icon: Icon, iconColor, label, value, subtitle, onClick }) => (
  <Card
    className={`relative overflow-hidden transition-all duration-200 ${onClick ? 'cursor-pointer hover:shadow-md hover:border-slate-300' : ''}`}
    onClick={onClick}
  >
    <div className="absolute top-0 right-0 w-24 h-24 -mr-6 -mt-6 rounded-full bg-gradient-to-br from-slate-50 to-slate-100 opacity-60" />
    <CardContent className="pt-5 pb-4 px-5">
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <p className="text-xs font-medium text-slate-500 uppercase tracking-wider">{label}</p>
          <p className="text-3xl font-bold text-slate-900 tracking-tight">{value}</p>
          {subtitle && (
            <p className="text-xs text-slate-500">{subtitle}</p>
          )}
        </div>
        <div className={`p-2.5 rounded-xl bg-gradient-to-br ${iconColor} shadow-sm`}>
          <Icon className="h-5 w-5 text-white" />
        </div>
      </div>
    </CardContent>
  </Card>
);

const StatusDot = ({ color }) => (
  <span className={`inline-block h-2 w-2 rounded-full ${color}`} />
);

const ProjectCard = ({ project, onClick }) => {
  const statusConfig = {
    active: { label: 'Active', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', dot: 'bg-emerald-500' },
    planning: { label: 'Planning', color: 'bg-blue-100 text-blue-700 border-blue-200', dot: 'bg-blue-500' },
    on_hold: { label: 'On Hold', color: 'bg-amber-100 text-amber-700 border-amber-200', dot: 'bg-amber-500' },
    completed: { label: 'Completed', color: 'bg-slate-100 text-slate-600 border-slate-200', dot: 'bg-slate-400' },
  };

  const sc = statusConfig[project.status] || statusConfig.active;

  return (
    <Card
      className="group cursor-pointer hover:shadow-md hover:border-slate-300 transition-all duration-200"
      onClick={() => onClick(project)}
    >
      <CardContent className="p-5">
        {/* Header Row */}
        <div className="flex items-start justify-between mb-3">
          <div className="flex-1 min-w-0 mr-3">
            <h3 className="text-sm font-semibold text-slate-900 truncate group-hover:text-blue-700 transition-colors">
              {project.name}
            </h3>
            {project.description && (
              <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">{project.description}</p>
            )}
          </div>
          <Badge variant="outline" className={`text-[10px] shrink-0 ${sc.color}`}>
            <StatusDot color={sc.dot} />
            <span className="ml-1">{sc.label}</span>
          </Badge>
        </div>

        {/* Member Roster */}
        <div className="mt-3 flex items-center gap-1">
          {project.assigned_users && project.assigned_users.length > 0 && (
            <div className="flex -space-x-2">
              {project.assigned_users.slice(0, 5).map((u) => (
                <TooltipProvider key={u.id} delayDuration={200}>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <div className="h-6 w-6 rounded-full bg-gradient-to-br from-slate-200 to-slate-300 border-2 border-white flex items-center justify-center ring-0">
                        <span className="text-[9px] font-bold text-slate-600">
                          {u.name?.split(' ').map(n => n[0]).join('').toUpperCase().slice(0, 2)}
                        </span>
                      </div>
                    </TooltipTrigger>
                    <TooltipContent side="bottom" className="text-xs">
                      <p className="font-medium">{u.name}</p>
                      <p className="text-slate-400">{u.role?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ')}</p>
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              ))}
              {project.user_count > 5 && (
                <div className="h-6 w-6 rounded-full bg-slate-100 border-2 border-white flex items-center justify-center">
                  <span className="text-[9px] font-medium text-slate-500">+{project.user_count - 5}</span>
                </div>
              )}
            </div>
          )}
          <span className="text-[10px] text-slate-400 ml-1">{project.user_count || 0} member{project.user_count !== 1 ? 's' : ''}</span>
        </div>
      </CardContent>
    </Card>
  );
};

const HealthScore = ({ stats }) => {
  const totalProjects = stats?.total_projects || 0;
  const totalUsers = stats?.total_users || 0;
  const activeUsers = stats?.users_by_status?.active || 0;
  const totalActivities = stats?.total_activities || 0;
  const completedActivities = stats?.activities_by_status?.completed || 0;
  const criticalActivities = stats?.critical_activities || 0;
  const totalSchedules = stats?.total_schedules || 0;

  let score = 50;
  if (totalUsers > 0) {
    score += Math.min(15, Math.round((activeUsers / totalUsers) * 15));
  }
  if (totalActivities > 0) {
    score += Math.min(20, Math.round((completedActivities / totalActivities) * 20));
  }
  if (totalProjects > 0) score += Math.min(10, totalProjects * 2);
  if (totalSchedules > 0) score += Math.min(5, totalSchedules);
  if (totalActivities > 0) {
    score -= Math.min(10, Math.round((criticalActivities / totalActivities) * 30));
  }
  score = Math.max(0, Math.min(100, score));

  const getScoreColor = (s) => {
    if (s >= 80) return { ring: 'text-emerald-500', bg: 'bg-emerald-50', label: 'Excellent', text: 'text-emerald-700' };
    if (s >= 60) return { ring: 'text-blue-500', bg: 'bg-blue-50', label: 'Good', text: 'text-blue-700' };
    if (s >= 40) return { ring: 'text-amber-500', bg: 'bg-amber-50', label: 'Fair', text: 'text-amber-700' };
    return { ring: 'text-red-500', bg: 'bg-red-50', label: 'Needs Attention', text: 'text-red-700' };
  };

  const config = getScoreColor(score);

  return (
    <div className="flex flex-col items-center text-center">
      <div className="relative">
        <svg className="w-28 h-28 -rotate-90" viewBox="0 0 100 100">
          <circle cx="50" cy="50" r="42" fill="none" strokeWidth="6" className="stroke-slate-100" />
          <circle
            cx="50" cy="50" r="42" fill="none" strokeWidth="6"
            className={config.ring.replace('text-', 'stroke-')}
            strokeLinecap="round"
            strokeDasharray={`${score * 2.64} 264`}
            style={{ transition: 'stroke-dasharray 1s ease-in-out' }}
          />
        </svg>
        <div className="absolute inset-0 flex flex-col items-center justify-center">
          <span className="text-2xl font-bold text-slate-900">{score}</span>
          <span className="text-[10px] text-slate-500 font-medium uppercase tracking-wider">Score</span>
        </div>
      </div>
      <Badge variant="outline" className={`mt-2 text-xs ${config.bg} ${config.text} border-transparent`}>
        {config.label}
      </Badge>
    </div>
  );
};

// Main Component

const AdminOverviewPage = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [stats, setStats] = useState(null);
  const [portfolio, setPortfolio] = useState([]);
  const [portfolioLoading, setPortfolioLoading] = useState(true);
  const [projectSearch, setProjectSearch] = useState('');
  const [expandedSection, setExpandedSection] = useState({ projects: true, activity: true });

  const fetchStats = useCallback(async () => {
    try {
      const res = await api.get('/admin/stats');
      setStats(res.data);
    } catch (err) {
      console.error('Failed to fetch stats:', err);
      toast.error('Failed to load admin statistics');
    } finally {
      setLoading(false);
    }
  }, []);

  const fetchPortfolio = useCallback(async () => {
    setPortfolioLoading(true);
    try {
      const res = await api.get('/admin/projects/portfolio');
      setPortfolio(res.data.projects || []);
    } catch (err) {
      console.error('Failed to fetch portfolio:', err);
    } finally {
      setPortfolioLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchStats();
    fetchPortfolio();
  }, [fetchStats, fetchPortfolio]);

  const formatRole = (role) => {
    return role?.split('_').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ') || '';
  };

  const formatTimestamp = (ts) => {
    const d = new Date(ts);
    const now = new Date();
    const diffMs = now - d;
    const diffMins = Math.floor(diffMs / 60000);
    const diffHours = Math.floor(diffMs / 3600000);
    const diffDays = Math.floor(diffMs / 86400000);

    if (diffMins < 1) return 'Just now';
    if (diffMins < 60) return `${diffMins}m ago`;
    if (diffHours < 24) return `${diffHours}h ago`;
    if (diffDays < 7) return `${diffDays}d ago`;
    return d.toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });
  };

  const getRoleBadgeColor = (role) => {
    switch (role) {
      case 'super_admin': return 'bg-red-100 text-red-700 border-red-200';
      case 'admin': return 'bg-blue-100 text-blue-700 border-blue-200';
      case 'member': return 'bg-slate-100 text-slate-700 border-slate-200';
      case 'plan': return 'bg-emerald-100 text-emerald-700 border-emerald-200';
      case 'collaborate': return 'bg-amber-100 text-amber-700 border-amber-200';
      case 'view': return 'bg-slate-100 text-slate-700 border-slate-200';
      default: return 'bg-slate-100 text-slate-600';
    }
  };

  const toggleSection = (section) => {
    setExpandedSection(prev => ({ ...prev, [section]: !prev[section] }));
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-[60vh]">
        <div className="text-center">
          <Loader2 className="h-8 w-8 animate-spin text-slate-400 mx-auto" />
          <p className="text-sm text-slate-400 mt-3">Loading admin dashboard...</p>
        </div>
      </div>
    );
  }

  const statusCounts = stats?.users_by_status || {};
  const roleCounts = stats?.users_by_role || {};
  const recentActivity = stats?.recent_activity || [];
  const totalUsers = stats?.total_users || 0;
  const activeUsers = statusCounts.active || 0;
  const invitedUsers = statusCounts.invited || 0;
  const suspendedUsers = statusCounts.suspended || 0;

  const totalProjects = stats?.total_projects || 0;
  const totalActivities = stats?.total_activities || 0;
  const criticalActivities = stats?.critical_activities || 0;
  const totalSchedules = stats?.total_schedules || 0;
  const totalBaselines = stats?.total_baselines || 0;
  const totalScenarios = stats?.total_scenarios || 0;
  const totalResources = stats?.total_resources || 0;

  const filteredPortfolio = portfolio.filter(p =>
    !projectSearch || p.name.toLowerCase().includes(projectSearch.toLowerCase())
  );

  return (
    <div className="space-y-6 max-w-[1400px] mx-auto">
      {/* HEADER */}
      <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h1 className="text-2xl font-bold text-slate-900 tracking-tight">Admin Overview</h1>
          <p className="text-sm text-slate-500 mt-0.5">Organization performance and management at a glance</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" size="sm" onClick={() => { fetchStats(); fetchPortfolio(); }}>
            <Activity className="h-3.5 w-3.5 mr-1.5" />
            Refresh
          </Button>
          <Button size="sm" onClick={() => navigate('/admin/users')}>
            <UserPlus className="h-3.5 w-3.5 mr-1.5" />
            Create Account
          </Button>
        </div>
      </div>

      {/* ROW 1: KEY METRICS */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          icon={Users}
          iconColor="from-blue-500 to-blue-600"
          label="Total Users"
          value={totalUsers}
          subtitle={`${activeUsers} active, ${invitedUsers} invited`}
          onClick={() => navigate('/admin/users')}
        />
        <StatCard
          icon={FolderOpen}
          iconColor="from-emerald-500 to-emerald-600"
          label="Projects"
          value={totalProjects}
          subtitle={`${totalSchedules} schedules across all`}
        />
        <StatCard
          icon={CalendarDays}
          iconColor="from-violet-500 to-violet-600"
          label="Activities"
          value={totalActivities}
          subtitle={criticalActivities > 0 ? `${criticalActivities} on critical path` : 'No critical activities'}
        />
        <StatCard
          icon={Layers}
          iconColor="from-amber-500 to-amber-600"
          label="Baselines"
          value={totalBaselines}
          subtitle={`${totalScenarios} scenarios, ${totalResources} resources`}
        />
      </div>

      {/* ROW 2: HEALTH + USER BREAKDOWN + QUICK ACTIONS */}
      <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
        {/* Organization Health Score */}
        <Card className="lg:col-span-3">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Gauge className="h-4 w-4 text-slate-400" />
              Org Health
            </CardTitle>
          </CardHeader>
          <CardContent className="flex justify-center py-2">
            <HealthScore stats={stats} />
          </CardContent>
        </Card>

        {/* Users Breakdown */}
        <Card className="lg:col-span-5">
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <Users className="h-4 w-4 text-slate-400" />
                Team Overview
              </CardTitle>
              <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => navigate('/admin/users')}>
                Manage <ArrowRight className="h-3 w-3 ml-1" />
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
              {/* Status Breakdown */}
              <div className="space-y-2">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">By Status</p>
                <div className="space-y-1.5">
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 flex-1 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full bg-emerald-500 transition-all" style={{ width: totalUsers > 0 ? `${(activeUsers / totalUsers) * 100}%` : '0%' }} />
                    </div>
                    <span className="text-xs font-medium text-slate-700 w-20 text-right">
                      <CheckCircle2 className="h-3 w-3 text-emerald-500 inline mr-1" />{activeUsers} Active
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 flex-1 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full bg-amber-500 transition-all" style={{ width: totalUsers > 0 ? `${(invitedUsers / totalUsers) * 100}%` : '0%' }} />
                    </div>
                    <span className="text-xs font-medium text-slate-700 w-20 text-right">
                      <Mail className="h-3 w-3 text-amber-500 inline mr-1" />{invitedUsers} Invited
                    </span>
                  </div>
                  <div className="flex items-center gap-2">
                    <div className="h-1.5 flex-1 rounded-full bg-slate-100 overflow-hidden">
                      <div className="h-full rounded-full bg-red-500 transition-all" style={{ width: totalUsers > 0 ? `${(suspendedUsers / totalUsers) * 100}%` : '0%' }} />
                    </div>
                    <span className="text-xs font-medium text-slate-700 w-20 text-right">
                      <UserX className="h-3 w-3 text-red-500 inline mr-1" />{suspendedUsers} Suspended
                    </span>
                  </div>
                </div>
              </div>

              {/* Role Breakdown */}
              <div className="space-y-2">
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest">By Role</p>
                <div className="space-y-1">
                  {Object.entries(roleCounts).sort((a, b) => b[1] - a[1]).map(([role, count]) => (
                    <div key={role} className="flex items-center justify-between">
                      <Badge variant="outline" className={`text-[10px] h-5 ${getRoleBadgeColor(role)}`}>
                        {formatRole(role)}
                      </Badge>
                      <span className="text-xs font-semibold text-slate-700">{count}</span>
                    </div>
                  ))}
                  {Object.keys(roleCounts).length === 0 && (
                    <p className="text-xs text-slate-400">No users yet</p>
                  )}
                </div>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Quick Actions */}
        <Card className="lg:col-span-4">
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <Zap className="h-4 w-4 text-amber-500" />
              Quick Actions
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-2 gap-2">
              <Button variant="outline" size="sm" className="justify-start h-9 text-xs" onClick={() => navigate('/admin/users')}>
                <UserPlus className="h-3.5 w-3.5 mr-2 text-blue-500" />
                Invite User
              </Button>
              <Button variant="outline" size="sm" className="justify-start h-9 text-xs" onClick={() => navigate('/projects')}>
                <FolderPlus className="h-3.5 w-3.5 mr-2 text-emerald-500" />
                New Project
              </Button>
              <Button variant="outline" size="sm" className="justify-start h-9 text-xs" onClick={() => navigate('/admin/activity-log')}>
                <Activity className="h-3.5 w-3.5 mr-2 text-violet-500" />
                Activity Log
              </Button>
              <Button variant="outline" size="sm" className="justify-start h-9 text-xs" onClick={() => navigate('/admin/settings')}>
                <Building2 className="h-3.5 w-3.5 mr-2 text-amber-500" />
                Org Settings
              </Button>
              <Button variant="outline" size="sm" className="justify-start h-9 text-xs" onClick={() => navigate('/import-export')}>
                <Upload className="h-3.5 w-3.5 mr-2 text-cyan-500" />
                Import Project
              </Button>
              <Button variant="outline" size="sm" className="justify-start h-9 text-xs" onClick={() => navigate('/schedules')}>
                <CalendarDays className="h-3.5 w-3.5 mr-2 text-rose-500" />
                Schedules
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ROW 3: PROJECT PORTFOLIO */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:justify-between">
            <button
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              onClick={() => toggleSection('projects')}
            >
              {expandedSection.projects ? (
                <ChevronDown className="h-4 w-4 text-slate-400" />
              ) : (
                <ChevronRight className="h-4 w-4 text-slate-400" />
              )}
              <CardTitle className="text-sm font-semibold">Project Portfolio</CardTitle>
              <Badge variant="outline" className="text-[10px] ml-1">{totalProjects} projects</Badge>
            </button>
            {expandedSection.projects && (
              <div className="flex items-center gap-2">
                <div className="relative flex-1 sm:flex-none">
                  <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 h-3.5 w-3.5 text-slate-400" />
                  <Input
                    placeholder="Search projects..."
                    value={projectSearch}
                    onChange={(e) => setProjectSearch(e.target.value)}
                    className="pl-8 h-8 w-full sm:w-48 text-xs"
                  />
                </div>
                <Button variant="outline" size="sm" className="h-8 text-xs" onClick={() => navigate('/projects')}>
                  <Eye className="h-3.5 w-3.5 mr-1.5" />
                  View All
                </Button>
              </div>
            )}
          </div>
        </CardHeader>
        {expandedSection.projects && (
          <CardContent>
            {portfolioLoading ? (
              <div className="flex justify-center py-12">
                <Loader2 className="h-6 w-6 animate-spin text-slate-400" />
              </div>
            ) : filteredPortfolio.length === 0 ? (
              <div className="text-center py-12">
                <FolderOpen className="h-10 w-10 text-slate-200 mx-auto mb-3" />
                <p className="text-sm font-medium text-slate-500">
                  {projectSearch ? 'No projects match your search' : 'No projects yet'}
                </p>
                <p className="text-xs text-slate-400 mt-1">
                  {projectSearch ? 'Try a different search term' : 'Create your first project to get started'}
                </p>
                {!projectSearch && (
                  <Button variant="outline" size="sm" className="mt-4" onClick={() => navigate('/projects')}>
                    <FolderPlus className="h-4 w-4 mr-2" />
                    Create Project
                  </Button>
                )}
              </div>
            ) : (
              <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
                {filteredPortfolio.map(project => (
                  <ProjectCard
                    key={project.id}
                    project={project}
                    onClick={(p) => navigate(`/projects/${p.id}`)}
                  />
                ))}
              </div>
            )}
          </CardContent>
        )}
      </Card>

      {/* ROW 4: PLATFORM METRICS + SCHEDULES */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Activity Summary */}
        <Card>
          <CardHeader className="pb-3">
            <div className="flex items-center justify-between">
              <CardTitle className="text-sm font-semibold flex items-center gap-2">
                <BarChart3 className="h-4 w-4 text-slate-400" />
                Activity Breakdown
              </CardTitle>
              <Badge variant="outline" className="text-[10px]">{totalActivities} total</Badge>
            </div>
          </CardHeader>
          <CardContent>
            {totalActivities === 0 ? (
              <p className="text-sm text-slate-400 text-center py-6">No activities recorded yet</p>
            ) : (
              <div className="space-y-4">
                {/* Visual bar breakdown */}
                <div className="h-3 rounded-full bg-slate-100 overflow-hidden flex">
                  {(stats?.activities_by_status?.completed || 0) > 0 && (
                    <div
                      className="h-full bg-emerald-500 transition-all"
                      style={{ width: `${((stats.activities_by_status.completed || 0) / totalActivities) * 100}%` }}
                    />
                  )}
                  {(stats?.activities_by_status?.in_progress || 0) > 0 && (
                    <div
                      className="h-full bg-blue-500 transition-all"
                      style={{ width: `${((stats.activities_by_status.in_progress || 0) / totalActivities) * 100}%` }}
                    />
                  )}
                  {(stats?.activities_by_status?.not_started || 0) > 0 && (
                    <div
                      className="h-full bg-slate-300 transition-all"
                      style={{ width: `${((stats.activities_by_status.not_started || 0) / totalActivities) * 100}%` }}
                    />
                  )}
                </div>
                <div className="grid grid-cols-3 gap-3 text-center">
                  <div className="py-2 rounded-lg bg-emerald-50">
                    <p className="text-lg font-bold text-emerald-700">{stats?.activities_by_status?.completed || 0}</p>
                    <p className="text-[10px] font-medium text-emerald-600">Completed</p>
                  </div>
                  <div className="py-2 rounded-lg bg-blue-50">
                    <p className="text-lg font-bold text-blue-700">{stats?.activities_by_status?.in_progress || 0}</p>
                    <p className="text-[10px] font-medium text-blue-600">In Progress</p>
                  </div>
                  <div className="py-2 rounded-lg bg-slate-50">
                    <p className="text-lg font-bold text-slate-700">{stats?.activities_by_status?.not_started || 0}</p>
                    <p className="text-[10px] font-medium text-slate-600">Not Started</p>
                  </div>
                </div>
                {criticalActivities > 0 && (
                  <div className="flex items-center gap-2 py-2 px-3 rounded-lg bg-red-50 border border-red-100">
                    <AlertTriangle className="h-4 w-4 text-red-500" />
                    <span className="text-xs font-medium text-red-700">
                      {criticalActivities} critical path activities require attention
                    </span>
                  </div>
                )}
              </div>
            )}
          </CardContent>
        </Card>

        {/* Schedules & Baselines */}
        <Card>
          <CardHeader className="pb-3">
            <CardTitle className="text-sm font-semibold flex items-center gap-2">
              <CalendarDays className="h-4 w-4 text-slate-400" />
              Schedules & Baselines
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {/* Schedules */}
              <div>
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-2">
                  Schedules <span className="text-slate-700 normal-case">({totalSchedules})</span>
                </p>
                <div className="grid grid-cols-3 gap-2">
                  <div className="text-center py-2 rounded-lg bg-slate-50 border border-slate-100">
                    <p className="text-base font-bold text-slate-500">{stats?.schedules_by_status?.draft || 0}</p>
                    <p className="text-[10px] text-slate-500">Draft</p>
                  </div>
                  <div className="text-center py-2 rounded-lg bg-emerald-50 border border-emerald-100">
                    <p className="text-base font-bold text-emerald-700">{stats?.schedules_by_status?.active || 0}</p>
                    <p className="text-[10px] text-emerald-600">Active</p>
                  </div>
                  <div className="text-center py-2 rounded-lg bg-amber-50 border border-amber-100">
                    <p className="text-base font-bold text-amber-700">{stats?.schedules_by_status?.archived || 0}</p>
                    <p className="text-[10px] text-amber-600">Archived</p>
                  </div>
                </div>
              </div>
              <Separator />
              {/* Baselines */}
              <div>
                <p className="text-[10px] font-semibold text-slate-400 uppercase tracking-widest mb-2">
                  Baselines <span className="text-slate-700 normal-case">({totalBaselines})</span>
                </p>
                <div className="grid grid-cols-2 gap-2">
                  <div className="text-center py-2 rounded-lg bg-slate-50 border border-slate-100">
                    <p className="text-base font-bold text-slate-500">{stats?.baselines_by_status?.draft || 0}</p>
                    <p className="text-[10px] text-slate-500">Draft</p>
                  </div>
                  <div className="text-center py-2 rounded-lg bg-emerald-50 border border-emerald-100">
                    <p className="text-base font-bold text-emerald-700">{stats?.baselines_by_status?.published || 0}</p>
                    <p className="text-[10px] text-emerald-600">Published</p>
                  </div>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* ROW 5: SYSTEM PULSE */}
      <Card>
        <CardHeader className="pb-3">
          <CardTitle className="text-sm font-semibold flex items-center gap-2">
            <Sparkles className="h-4 w-4 text-violet-500" />
            System Pulse
          </CardTitle>
          <CardDescription className="text-xs">Platform-wide feature utilization</CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
            {[
              { icon: Network, color: 'text-indigo-500', bg: 'bg-indigo-50', label: 'WBS Nodes', value: stats?.total_wbs || 0 },
              { icon: GitBranch, color: 'text-purple-500', bg: 'bg-purple-50', label: 'Relationships', value: stats?.total_relationships || 0 },
              { icon: GitBranch, color: 'text-violet-500', bg: 'bg-violet-50', label: 'Scenarios', value: totalScenarios },
              { icon: Cpu, color: 'text-blue-500', bg: 'bg-blue-50', label: 'Simulations', value: stats?.total_simulations || 0 },
              { icon: Bot, color: 'text-emerald-500', bg: 'bg-emerald-50', label: 'AI Chats', value: stats?.total_ai_conversations || 0 },
              { icon: FileUp, color: 'text-amber-500', bg: 'bg-amber-50', label: 'Imports', value: stats?.total_imports || 0 },
            ].map(({ icon: Icon, color, bg, label, value }) => (
              <div key={label} className={`text-center py-3 rounded-xl ${bg} border border-transparent hover:border-slate-200 transition-colors`}>
                <Icon className={`h-5 w-5 ${color} mx-auto mb-1.5`} />
                <p className="text-lg font-bold text-slate-900">{value}</p>
                <p className="text-[10px] font-medium text-slate-500">{label}</p>
              </div>
            ))}
          </div>
          {/* Secondary metrics row */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mt-3">
            <div className="flex items-center gap-2.5 py-2 px-3 rounded-lg bg-slate-50">
              <CalendarDays className="h-4 w-4 text-blue-500" />
              <div>
                <p className="text-xs font-bold text-slate-900">{stats?.total_calendars || 0}</p>
                <p className="text-[10px] text-slate-500">Calendars</p>
              </div>
            </div>
            <div className="flex items-center gap-2.5 py-2 px-3 rounded-lg bg-slate-50">
              <BellRing className="h-4 w-4 text-amber-500" />
              <div>
                <p className="text-xs font-bold text-slate-900">{stats?.total_notifications || 0}</p>
                <p className="text-[10px] text-slate-500">Notifications</p>
              </div>
            </div>
            <div className="flex items-center gap-2.5 py-2 px-3 rounded-lg bg-slate-50">
              <History className="h-4 w-4 text-slate-500" />
              <div>
                <p className="text-xs font-bold text-slate-900">{stats?.total_history_entries || 0}</p>
                <p className="text-[10px] text-slate-500">History Entries</p>
              </div>
            </div>
            <div className="flex items-center gap-2.5 py-2 px-3 rounded-lg bg-slate-50">
              <Layers className="h-4 w-4 text-rose-500" />
              <div>
                <p className="text-xs font-bold text-slate-900">{totalResources}</p>
                <p className="text-[10px] text-slate-500">Resources</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* ROW 6: RECENT ACTIVITY */}
      <Card>
        <CardHeader className="pb-3">
          <div className="flex items-center justify-between">
            <button
              className="flex items-center gap-2 hover:opacity-80 transition-opacity"
              onClick={() => toggleSection('activity')}
            >
              {expandedSection.activity ? (
                <ChevronDown className="h-4 w-4 text-slate-400" />
              ) : (
                <ChevronRight className="h-4 w-4 text-slate-400" />
              )}
              <CardTitle className="text-sm font-semibold">Recent Activity</CardTitle>
            </button>
            <Button variant="ghost" size="sm" className="text-xs h-7" onClick={() => navigate('/admin/activity-log')}>
              View all <ArrowRight className="h-3 w-3 ml-1" />
            </Button>
          </div>
        </CardHeader>
        {expandedSection.activity && (
          <CardContent>
            {recentActivity.length === 0 ? (
              <div className="text-center py-10">
                <Activity className="h-8 w-8 text-slate-200 mx-auto mb-2" />
                <p className="text-sm font-medium text-slate-400">No activity recorded yet</p>
                <p className="text-xs text-slate-400 mt-1">Admin actions will appear here as they happen</p>
              </div>
            ) : (
              <div className="relative">
                {/* Timeline line */}
                <div className="absolute left-[17px] top-3 bottom-3 w-px bg-slate-100" />
                <div className="space-y-0">
                  {recentActivity.map((entry) => {
                    const actionConfig = ACTION_ICONS[entry.action] || { icon: Activity, color: 'text-slate-400' };
                    const Icon = actionConfig.icon;
                    const label = ACTION_LABELS[entry.action] || entry.action;
                    return (
                      <div key={entry.id} className="relative flex items-start gap-4 py-3 group">
                        {/* Timeline dot */}
                        <div className="relative z-10 mt-0.5">
                          <div className="p-1.5 rounded-full bg-white border-2 border-slate-200 group-hover:border-slate-300 transition-colors">
                            <Icon className={`h-3 w-3 ${actionConfig.color}`} />
                          </div>
                        </div>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm text-slate-700">
                            <span className="font-semibold text-slate-900">{entry.actor_name}</span>
                            {' '}<span className="text-slate-500">{label}</span>
                            {entry.target_name && (
                              <span className="font-semibold text-slate-900"> {entry.target_name}</span>
                            )}
                          </p>
                          {entry.details && (
                            <p className="text-xs text-slate-400 mt-0.5 truncate">{entry.details}</p>
                          )}
                        </div>
                        <span className="text-[10px] text-slate-400 whitespace-nowrap mt-0.5 font-medium">
                          {formatTimestamp(entry.timestamp)}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            )}
          </CardContent>
        )}
      </Card>
    </div>
  );
};

export default AdminOverviewPage;
